import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/formatUtils';
import type { SpendingBreakdown } from '@/types/dashboard';

interface SpendingBreakdownCardProps {
  data: SpendingBreakdown;
  insight?: string;
}

interface ProgressBarProps {
  percent: number;
  target?: number;
  color: 'green' | 'amber' | 'gray';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ percent, target, color }) => {
  const colorClasses = {
    green: 'bg-emerald-500',
    amber: 'bg-amber-500',
    gray: 'bg-gray-400',
  };

  const isOverTarget = target && percent > target;

  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div 
        className={cn(
          'h-full rounded-full transition-all',
          isOverTarget ? 'bg-amber-500' : colorClasses[color]
        )}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
};

interface CategoryRowProps {
  label: string;
  percent: number;
  amount: number;
  target?: number;
  color: 'green' | 'amber' | 'gray';
}

const CategoryRow: React.FC<CategoryRowProps> = ({ label, percent, amount, target, color }) => {
  const isOverTarget = target && percent > target;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground font-mono">
            {formatCurrency(amount)}
          </span>
          <span className={cn(
            'text-sm font-semibold',
            isOverTarget ? 'text-amber-600' : 'text-foreground',
          )}>
            {percent.toFixed(1)}%
          </span>
          {target != null && (
            <span className="text-xs text-muted-foreground">
              / {target}%
            </span>
          )}
        </div>
      </div>
      <ProgressBar
        percent={percent}
        target={target}
        color={isOverTarget ? 'amber' : color}
      />
    </div>
  );
};

export const SpendingBreakdownCard: React.FC<SpendingBreakdownCardProps> = ({
  data,
  insight,
}) => {
  return (
    <Card className="bg-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            What Did I Spend On
          </span>
        </div>

        <div className="space-y-5">
          <CategoryRow
            label="Labor"
            percent={data.labor.percent}
            amount={data.labor.amount}
            target={data.labor.target}
            color="green"
          />
          <CategoryRow
            label="Food + Packaging"
            percent={data.foodAndPackaging.percent}
            amount={data.foodAndPackaging.amount}
            target={data.foodAndPackaging.target}
            color="green"
          />
          <CategoryRow
            label="Operational Expenses"
            percent={data.operationalExpenses.percent}
            amount={data.operationalExpenses.amount}
            color="gray"
          />
          <CategoryRow
            label="Non-Business"
            percent={data.nonBusiness.percent}
            amount={data.nonBusiness.amount}
            color="gray"
          />
        </div>

        {insight && (
          <p className="text-sm text-muted-foreground mt-6">
            {insight}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
